import GL from "_src/engine/gl";
import shaderPreprocessor from "_src/engine/shader_preprocessor.js";
import extensions from "_src/engine/extensions";
import compat from "_src/engine/compat";
import gl_types from "_src/engine/gl_types";
import { generateHashCode } from "_src/engine/hash";
import {uniformSource,
  blendFactor,
  blendEquation,
  stencilAction,
  stencilFunc,
  triangleSide,
  cullingFrontFace,
  uniformType} from "_src/engine/constants";
import output from "_src/engine/output";

import { vec2, vec3, vec4, mat2, mat3, mat4 } from "_src/libs/gl-matrix";

const WEBGL2_SYSTEM_GENERATED_VALUE = ["gl_VertexID", "gl_InstanceID"];
const WEBGL1_SYSTEM_GENERATED_VALUE = [];

export default class Shader {

  constructor(params) {

    this.sceneUniformsSetters = [];
    this.objectUniformsSetters = [];
    this.chunkUniformsSetters = [];
    this.shaderUniformsSetters = [];

    this.id = 0;

    this.uniforms = {};

    this.attributesLocations = {};

    //rendering props
    this.backFaceCulling = params.backFaceCulling != undefined ? params.backFaceCulling : true;

    this.transparent = params.transparent || false;

    this.drawColor = params.drawColor != undefined ? params.drawColor : true;

    this.depthTest = params.depthTest != undefined ? params.depthTest : true;
    this.stencilTest = false;
    this.scissorTest = false;
    this.scissor = [0,0,0,0];
    this.polygonOffsetEnabled = false;
    this.polygonOffset = [0,0];
    this.frontFace = gl_types.getTriangleSide(triangleSide.FRONT);
    this.backFace = gl_types.getTriangleSide(triangleSide.BACK);
    // front
    this.stencilFrontFail = gl_types.getStencilAction(stencilAction.KEEP);
    this.stencilFrontZFail = gl_types.getStencilAction(stencilAction.KEEP);
    this.stencilFrontZPass = gl_types.getStencilAction(stencilAction.KEEP);
    // back
    this.stencilBackFail = gl_types.getStencilAction(stencilAction.KEEP);
    this.stencilBackZFail = gl_types.getStencilAction(stencilAction.KEEP);
    this.stencilBackZPass = gl_types.getStencilAction(stencilAction.KEEP);

    this.stencilFunc = gl_types.getStencilFunction(stencilFunc.ALWAYS);
    this.stencilReferenceVal = 1;
    this.stencilTestMask = 0xFF;
    this.stencilUpdateMask = 0xFF;

    this.sourceBlendFactor = params.sourceBlendFactor !== undefined ?
        gl_types.getBlendFactor(params.sourceBlendFactor) :
        gl_types.getBlendFactor(blendFactor.SRC_ALPHA);
    this.destBlendFactor = params.destBlendFactor !== undefined ?
        gl_types.getBlendFactor(params.destBlendFactor) :
        gl_types.getBlendFactor(blendFactor.ONE_MINUS_SRC_ALPHA);

    this.blendEquationRgb = gl_types.getBlendEquation(blendEquation.FUNC_ADD);
    this.blendEquationAlpha = gl_types.getBlendEquation(blendEquation.FUNC_ADD);

    this.needsUpdate = true;

    // hardware props
    this.mrtEnabled = false;
    this.standardDerivativesEnabled = false;
    this.textureLodSamplingEnabled = false;
    this.writeDepth = false;

    this.generalOutputEnabled = false;

    this.cullingFrontFace = gl_types.getFrontFace(cullingFrontFace.COUNTER_CLOCK_WISE);

    this.instancedMode = params.instancedMode != undefined ? params.instancedMode : false;
    this.instanceDivisor = 0;
    this.instancedAttributesRemapping = {};

    this.program = null;
    this._directives = {};
    this._dependentDirectives = new Set();
    this._textures = [];
    this.initDirectives(params);

  }

  renderCallback() {}

  setVectorUniform(name, value, index) {

    const uniform = this.uniforms[name];

    if (!uniform) {
      output.error(output.DEVELOPERS_ERROR, `Couldn't find uniform ${name}`);
    }

    switch(uniform.type) {
      case uniformType.FLOAT1V:
      case uniformType.INT1V:
        uniform.value[index] = value;
        break;
      case uniformType.FLOAT2V:
      case uniformType.INT2V:
        uniform.value[2 * index] = value[0];
        uniform.value[2 * index + 1] = value[1];
        break;
      case uniformType.FLOAT3V:
      case uniformType.INT3V:
        uniform.value[3 * index] = value[0];
        uniform.value[3 * index + 1] = value[1];
        uniform.value[3 * index + 2] = value[2];
        break;
      case uniformType.FLOAT4V:
      case uniformType.INT4V:
        uniform.value[4 * index] = value[0];
        uniform.value[4 * index + 1] = value[1];
        uniform.value[4 * index + 2] = value[2];
        uniform.value[4 * index + 3] = value[3];
        break;
      default:
        output.error(output.DEVELOPERS_ERROR, `Unknown uniform type ${uniform.type}`);
        return;
    }

    this.dropID();

  }

  setUniform(name, value) {

    const uniform = this.uniforms[name];

    if (!uniform) {
      output.error(output.DEVELOPERS_ERROR, `Couldn't find uniform ${name}`);
    }

    switch(uniform.type) {
      case uniformType.FLOAT1:
      case uniformType.INT1:
        uniform.value = value;
        break;
      case uniformType.FLOAT2:
      case uniformType.INT2:
        vec2.copy(uniform.value, value);
        break;
      case uniformType.FLOAT3:
      case uniformType.INT3:
        vec3.copy(uniform.value, value);
        break;
      case uniformType.FLOAT4:
      case uniformType.INT4:
        vec4.copy(uniform.value, value);
        break;

      case uniformType.MAT2:
        mat2.copy(uniform.value, value);
        break;
      case uniformType.MAT3:
        mat3.copy(uniform.value, value);
        break;
      case uniformType.MAT4:
        mat4.copy(uniform.value, value);
        break;
      default:
        output.error(output.DEVELOPERS_ERROR, `Unknown uniform type ${uniform.type}`);
        return;
    }

    this.dropID();

  }

  //function for reimplementation
  initDirectives() {}

  setDirective(name, value) {
    if (this._dependentDirectives.has(name)) {
      output.error(output.DEVELOPERS_ERROR, "Directive " + name + " can't be set directly. Use shader method.");
      return;
    }

    this._setDirective(name, value);
  }

  getDirectiveValue(name) {
    return this._directives[name];
  }

  _setDirective(name, value) {
    if (!(name in this._directives)) {
      output.error(output.DEVELOPERS_ERROR, "Unknown shader directive: " + name);
      return;
    }

    if (this._directives[name] === value) {
      return;
    }

    this._directives[name] = value;
    this.needsUpdate = true;
    this.dropID();
  }

  dropID() {
    this.id = 0;
  }

  generateUniqueID() {
    this.id = generateHashCode(this, 0);
    return this.id;
  }

  setupTextures() {
    if (this._textures.length) {
      GL.setupTextures(this._textures);
    }
  }

  setSourceBlendFactor(sfactor) {
    this.sourceBlendFactor = gl_types.getBlendFactor(sfactor);
  }

  setDestBlendFactor(dfactor) {
    this.destBlendFactor = gl_types.getBlendFactor(dfactor);
  }

  setBlendEquation(funcRgb, funcAlpha) {
    this.blendEquationRgb = gl_types.getBlendEquation(funcRgb);
    this.blendEquationAlpha = gl_types.getBlendEquation(funcAlpha);
    this.dropID();
  }

  enableStencilTest() {
    this.stencilTest = true;
    this.dropID();
  }

  disableStencilTest() {
    this.stencilTest = false;
    this.dropID();
  }

  enableDepthTest() {
    this.depthTest = true;
    this.dropID();
  }

  disableDepthTest() {
    this.depthTest = false;
    this.dropID();
  }

  enableScissorTest() {
    this.scissorTest = true;
    this.dropID();
  }

  disableScissorTest() {
    this.scissorTest = false;
    this.dropID();
  }

  setScissor(scissor) {
    this.scissor = scissor;
  }

  enablePolygonOffset() {
    this.polygonOffsetEnabled = true;
    this.dropID();
  }

  disablePolygonOffset() {
    this.polygonOffsetEnabled = false;
    this.dropID();
  }

  setPolygonOffset(factor, units) {
    this.polygonOffset = [factor, units];
  }

  setStencilFrontFailAction(action) {
    this.stencilFrontFail = gl_types.getStencilAction(action);
    this.dropID();
  }

  setStencilFrontZFailAction(action) {
    this.stencilFrontZFail = gl_types.getStencilAction(action);
    this.dropID();
  }

  setStencilFrontZPassAction(action) {
    this.stencilFrontZPass = gl_types.getStencilAction(action);
    this.dropID();
  }

  setStencilBackFailAction(action) {
    this.stencilBackFail = gl_types.getStencilAction(action);
    this.dropID();
  }

  setStencilBackZFailAction(action) {
    this.stencilBackZFail = gl_types.getStencilAction(action);
    this.dropID();
  }

  setStencilBackZPassAction(action) {
    this.stencilBackZPass = gl_types.getStencilAction(action);
    this.dropID();
  }

  setStencilFunction(func) {
    this.stencilFunc = gl_types.getStencilFunction(func);
    this.dropID();
  }

  setStencilReferenceValue(ref) {
    this.stencilReferenceVal = ref;
    this.dropID();
  }

  setStencilTestMask(mask) {
    this.stencilTestMask = mask;
    this.dropID();
  }

  setStencilUpdateMask(mask) {
    this.stencilUpdateMask = mask;
    this.dropID();
  }

  setCullingFrontFace(frontFace) {
    this.cullingFrontFace = gl_types.getFrontFace(frontFace);
    this.dropID();
  }

  compile(vertText, fragText, customPreprocessRules = {}) {

    if (!vertText || !fragText) {
      throw "Can't construct a shader. Missing shader texts.";
    }

    let processedVertexText, processedFragmentText;

    const directivesText = extractDirectivesText(this);

    processedVertexText = directivesText + "\n" + vertText;
    processedFragmentText = directivesText + "\n" + fragText;

    if (GL.checkWebGL2()) {

      processedVertexText = shaderPreprocessor.castVShaderToWebGL2(processedVertexText);
      processedFragmentText = shaderPreprocessor.castFShaderToWebGL2(processedFragmentText);

    } else {

      processedVertexText = shaderPreprocessor.castVShaderToWebGL1(processedVertexText);
      processedFragmentText = shaderPreprocessor.castFShaderToWebGL1(processedFragmentText);

    }

    processedVertexText = shaderPreprocessor.applyCustomRules(processedVertexText,
                                                              customPreprocessRules);
    processedFragmentText = shaderPreprocessor.applyCustomRules(processedFragmentText,
                                                                customPreprocessRules);

    const gl = GL.getContext();

    const program = gl.createProgram();

    const vertShader = compileShader(gl, processedVertexText, gl.VERTEX_SHADER);
    const fragShader = compileShader(gl, processedFragmentText, gl.FRAGMENT_SHADER);

    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);

    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {

      const info = gl.getProgramInfoLog(program);
      output.error(output.DEVELOPERS_ERROR, "Could not compile shader program. \n\n" + info);
      gl.deleteProgram(program);
      gl.deleteShader(vertShader);
      gl.deleteShader(fragShader);

      return;

    }

    this.program = program;
    this.needsUpdate = false;

    this._collectAttributes(gl);
    this._cleanupUniformSetters();
    this._collectUniforms(gl);

  }

  // these methods should be implemented by derived shaders
  updateSceneUniformValues()  { }
  updateObjectUniformValues() { }
  updateChunkUniformValues()  { }

  updateSceneDirectives()  { }

  _collectUniforms(gl) {

    var uniNumber = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORMS);

    for (let i = 0; i < uniNumber; ++i) {

      const uni = gl.getActiveUniform(this.program, i);
      const uniformName = uni.name;
      const uniformLocation = gl.getUniformLocation(this.program, uniformName);

      const uniform = this.uniforms[uniformName];

      if (!uniform) {
        output.error(output.DEVELOPERS_ERROR, "Shader program requires the following uniform to be added: \"" + uniformName + "\"");
        continue;
      }

      uniform.location = uniformLocation;

      switch (uniform.source) {
      case uniformSource.SCENE:
        this.sceneUniformsSetters.push(uniform);
        break;
      case uniformSource.SHADER:
        this.shaderUniformsSetters.push(uniform);
        break;
      case uniformSource.OBJECT:
        this.objectUniformsSetters.push(uniform);
        break;
      case uniformSource.CHUNK:
        this.chunkUniformsSetters.push(uniform);
        break;
      }

    }

  }

  _cleanupUniformSetters() {
    this.sceneUniformsSetters.length = 0;
    this.objectUniformsSetters.length = 0;
    this.chunkUniformsSetters.length = 0;
    this.shaderUniformsSetters.length = 0;
  }

  _collectAttributes(gl) {

    this.attributesLocations = {};

    const systemGeneratedValue = GL.checkWebGL2() ? WEBGL2_SYSTEM_GENERATED_VALUE : WEBGL1_SYSTEM_GENERATED_VALUE;

    var attrNumber = gl.getProgramParameter(this.program, gl.ACTIVE_ATTRIBUTES);

    for (let i = 0; i < attrNumber; ++i) {

      var attr = gl.getActiveAttrib(this.program, i);

      if (systemGeneratedValue.indexOf(attr.name) != -1) {
        continue;
      }

      this.attributesLocations[attr.name] = gl.getAttribLocation(this.program, attr.name);

    }

  }

}

function compileShader(gl, text, type) {

  const shader = gl.createShader(type);

  gl.shaderSource(shader, text);
  gl.compileShader(shader);

  const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);

  if (!success) {

    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      lines[i] = (i + 1) + " " + lines[i];
    }
    text = lines.join("\n");

    throw "Could not compile shader:" + gl.getShaderInfoLog(shader) + "\n" + text;
  }

  return shader;

}

function extractDirectivesText(shader) {

  let combinedDirectives = combineDirectives(shader);

  return combinedDirectives.join("\n");

}

function combineDirectives(shader) {

  const result = [];
  const shaderDirectives = [];
  const serviceDirectives = [];

  const isWebGL2 = GL.checkWebGL2();
  const standardDerivatives = extensions.get("OES_standard_derivatives");

  for (const directive in shader._directives) {

    const value = shader._directives[directive];
    shaderDirectives.push("#define " + directive + " " + toShaderValue(value));
  }

  if (isWebGL2) {
    serviceDirectives.push("#version 300 es");
  }
  shaderDirectives.push("#define WEBGL2 " + toShaderValue(isWebGL2));

  if (shader.mrtEnabled && compat.mrtEnabled) {

    if (!isWebGL2) {
      serviceDirectives.push("#extension GL_EXT_draw_buffers : require");
    }

  }
  shaderDirectives.push("#define MULTIPLE_RENDER_TARGETS " + toShaderValue(shader.mrtEnabled && compat.mrtEnabled));
  shaderDirectives.push("#define CUSTOM_OUTPUT " + toShaderValue(shader.generalOutputEnabled));

  if (shader.standardDerivativesEnabled && standardDerivatives) {

    if (!isWebGL2) {
      serviceDirectives.push("#extension GL_OES_standard_derivatives : enable");
    }
  }

  if (shader.textureLodSamplingEnabled && !isWebGL2) {
    extensions.get("EXT_shader_texture_lod");
    serviceDirectives.push("#extension GL_EXT_shader_texture_lod : enable");
  }
  shaderDirectives.push("#define STANDARD_DERIVATIVES " + toShaderValue(shader.standardDerivativesEnabled && Boolean(standardDerivatives)));

  if (shader.writeDepth && extensions.checkDepthWrite() && !isWebGL2) {
    serviceDirectives.push("#extension GL_EXT_frag_depth:enable");
  }

  result.push.apply(result, serviceDirectives);
  result.push.apply(result, shaderDirectives);

  return result;

}

function toShaderValue(value) {
  switch (typeof value) {
    case "number":
      return parseFloat(value);
    case "boolean":
      return value ? 1 : 0;
    default:
      return value ? value: 0;
  }
}

